import { Injectable, computed, signal } from '@angular/core';
import { Repair, RepairLog, RepairStatus } from '../models/repair.model';
import type { IRepairsStore } from './repairs-store.interface';

@Injectable({ providedIn: 'root' })
export class RepairsMockStore implements IRepairsStore {
  private readonly _repairs = signal<readonly Repair[]>([]);
  private readonly _logs = signal<Record<string, readonly RepairLog[]>>({});
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly repairs = this._repairs.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly totalCount = computed(() => this._repairs().length);
  readonly openCount = computed(() =>
    this._repairs().filter(r => r.status !== RepairStatus.Completed && r.status !== RepairStatus.Canceled).length
  );
  readonly completedCount = computed(() =>
    this._repairs().filter(r => r.status === RepairStatus.Completed).length
  );

  readonly counts = computed(() => {
    const out = {
      pending: 0,
      inProgress: 0,
      completed: 0,
      canceled: 0,
      waitingForParts: 0,
      assigned: 0,
      resolved: 0,
      total: 0
    };
    for (const r of this._repairs()) {
      out.total++;
      switch (r.status) {
        case RepairStatus.Pending: out.pending++; break;
        case RepairStatus.InProgress: out.inProgress++; break;
        case RepairStatus.Completed: out.completed++; break;
        case RepairStatus.Canceled: out.canceled++; break;
        case RepairStatus.WaitingForParts: out.waitingForParts++; break;
        case RepairStatus.Assigned: out.assigned++; break;
        case RepairStatus.Resolved: out.resolved++; break;
      }
    }
    return out;
  });

  async refresh(): Promise<void> {
    this._loading.set(true);
    await Promise.resolve();
    this._loading.set(false);
  }

  getRepairById(repairUID: string): Repair | null {
    return this._repairs().find(r => r.repairUID === repairUID) ?? null;
  }

  getRepairsByProperty(propertyUID: string): readonly Repair[] {
    return this._repairs().filter(r => r.propertyUID === propertyUID);
  }

  getRepairsByStatus(status: RepairStatus): readonly Repair[] {
    return this._repairs().filter(r => r.status === status);
  }

  async addRepair(repair: Repair): Promise<string> {
    const uid = `mock-rep-${Math.random().toString(36).slice(2, 10)}`;
    const next = Object.assign(
      Object.create(Object.getPrototypeOf(repair) || Object.prototype),
      repair,
      { repairUID: uid, createdAt: new Date(), updatedAt: new Date() }
    ) as Repair;
    this._repairs.set([...this._repairs(), next]);
    return uid;
  }

  async updateRepair(repair: Repair): Promise<void> {
    if (!repair.repairUID) throw new Error('repairUID required');
    const uid = repair.repairUID;
    this._repairs.set(
      this._repairs().map(r => (r.repairUID === uid ? ({ ...r, ...repair, updatedAt: new Date() } as Repair) : r))
    );
  }

  async deleteRepair(repairUID: string): Promise<void> {
    this._repairs.set(this._repairs().filter(r => r.repairUID !== repairUID));
    const map = { ...this._logs() };
    delete map[repairUID];
    this._logs.set(map);
  }

  async getRepairLogs(repairUID: string): Promise<readonly RepairLog[]> {
    return this._logs()[repairUID] ?? [];
  }

  async addGeneralLog(repairUID: string, action: string, notes?: string, documentUIDs?: string[]): Promise<string> {
    const uid = `mock-log-${Math.random().toString(36).slice(2, 10)}`;
    const log = {
      repairUID,
      logUID: uid,
      date: new Date(),
      action,
      notes,
      userUID: 'mock',
      userName: 'mock',
      documentUIDs: documentUIDs ?? []
    } as unknown as RepairLog;
    const map = { ...this._logs(), [repairUID]: [log, ...(this._logs()[repairUID] ?? [])] };
    this._logs.set(map);
    return uid;
  }

  /** Test helper. */
  _seed(repairs: readonly Repair[]): void {
    this._repairs.set(repairs);
  }
}
